// ============================================================
// THE LOOKS ON THE FIGURE (plan body-map M1; tools/plate-art/canvas/map/
// auras.html): Fear's growing outlines, Swift's afterimages and streaks
// (under it), and over it the psyker's light and lightning, the daemon's
// echoes and corona, the conduit's motes, Toxic's flash, Frenzied's halo and
// From Beyond's head window. Screen pixels, from the foot and the head.
// The many-piece drawings are memoised as on the ground (GroundLooks).
// ============================================================

import { memo } from 'react';
import { boltPath } from '../../../engine/display/looks';
import lk from '../../../styles/DisplayLooks.module.css';
import { VoidWindow } from './GroundLooks';
import { headFromFoot, height, metres, onCircle, px, squash, TAU, type LookPlace, type Vars } from './place';

/** A copy of the figure's stalk and head, drawn from its foot (the origin). */
function StalkHead({ p }: { p: LookPlace }) {
  const h = headFromFoot(p);
  return (
    <>
      <line x2={px(h.x)} y2={px(h.y)} />
      <circle cx={px(h.x)} cy={px(h.y)} r={px(p.hr)} />
    </>
  );
}

/** The figure's outline - ring, stalk, head - drawn from its foot (the origin). */
function Outline({ p, className, style }: { p: LookPlace; className: string; style?: Vars }) {
  return (
    <g className={className} style={style}>
      <ellipse rx={px(p.rx)} ry={px(p.ry)} />
      <StalkHead p={p} />
    </g>
  );
}

/** Fear: four outlines of itself growing out from its feet and fading, each a quarter-beat behind the last. */
export function FearOutlines({ p }: { p: LookPlace }) {
  return (
    <g transform={`translate(${px(p.foot.x)} ${px(p.foot.y)})`} data-look-part="fear">
      {[0, 1, 2, 3].map((i) => (
        <Outline key={i} p={p} className={lk.fear} style={{ animationDelay: `${-(i * 4.5) / 4}s` }} />
      ))}
    </g>
  );
}

/**
 * Swift: three afterimages pacing either side of it, and streaks behind. The marker itself does not move - its hit area
 * must stay on its square - so the pacing is the afterimages'.
 */
export function SwiftGhosts({ p }: { p: LookPlace }) {
  const reach = `${px(metres(p, 1.3) / 2)}px`;
  return (
    <g transform={`translate(${px(p.foot.x)} ${px(p.foot.y)})`} data-look-part="swift">
      {[1, 2, 3].map((k) => (
        <g
          key={k}
          className={lk.afterimage}
          style={{ '--reach': reach, animationDelay: `${-0.07 * k}s`, opacity: 0.25 / k } as Vars}
        >
          <StalkHead p={p} />
        </g>
      ))}
      {[0.25, 0.55, 0.85].map((m) => {
        const y = -height(p, m);
        return (
          <line key={m} className={lk.streak} x1={px(-metres(p, 0.5))} y1={px(y)} x2={px(-metres(p, 1.3))} y2={px(y)} />
        );
      })}
    </g>
  );
}

/** The psyker's light round its head, and a flick of lightning now and then (the design's, at any rating). */
export function PsykerHead({ p, seed }: { p: LookPlace; seed: string }) {
  return (
    <g transform={`translate(${px(p.head.x)} ${px(p.head.y)})`} data-look-part="psyker-head">
      <circle className={lk.psyLight} r={px(p.hr * 2.3)} />
      <path className={lk.bolt} d={boltPath(seed)} transform={`scale(${(p.hr / 7.2).toFixed(3)})`} />
    </g>
  );
}

/** The daemon over its figure: two echoes of it jittering, and a corona of rays turning at its head. */
export function DaemonEchoes({ p }: { p: LookPlace }) {
  return (
    <g data-look-part="daemon">
      <g transform={`translate(${px(p.foot.x)} ${px(p.foot.y)})`}>
        {[0, 1].map((k) => (
          <g key={k} className={lk.echo} style={{ animationDelay: `${-0.4 * k}s` }}>
            <StalkHead p={p} />
          </g>
        ))}
      </g>
      <g transform={`translate(${px(p.head.x)} ${px(p.head.y)})`}>
        <Corona hr={p.hr} />
      </g>
    </g>
  );
}

const Corona = memo(function Corona({ hr }: { hr: number }) {
  return (
    <g className={lk.corona}>
      {Array.from({ length: 14 }, (_, i) => {
        const a = (i / 14) * TAU;
        const [x0, y0] = onCircle(hr * 1.3, a);
        const [x1, y1] = onCircle(hr * (1.9 + 0.45 * Math.sin(i * 1.7)), a);
        return <line key={i} className={lk.ray} x1={px(x0)} y1={px(y0)} x2={px(x1)} y2={px(y1)} />;
      })}
    </g>
  );
});

/** Warp Conduit: motes pouring out of its head and falling outward to the ground. */
export function ConduitMotes({ p }: { p: LookPlace }) {
  return (
    <g transform={`translate(${px(p.head.x)} ${px(p.head.y)})`} data-look-part="conduit-motes">
      {/* The fall in whole pixels: under perspective it changes with depth, and each change restyles every mote. */}
      <Motes spread={metres(p, 0.85)} ky={squash(p)} fall={Math.round(-headFromFoot(p).y)} />
    </g>
  );
}

const Motes = memo(function Motes({ spread, ky, fall }: { spread: number; ky: number; fall: number }) {
  return Array.from({ length: 18 }, (_, i) => {
    const [gx, gz] = onCircle(spread, i * 2.4);
    const style: Vars = {
      '--tx': `${px(gx)}px`,
      '--ty': `${px(fall + gz * ky)}px`,
      animationDelay: `${-((i / 18) * 2).toFixed(2)}s`,
    };
    return <circle key={i} className={lk.mote} r={1.6} style={style} />;
  });
});

/** A head-sized mark at the head: Toxic's flash with every beat, Frenzied's red halo. */
export function HeadMark({ p, className, grow = 0 }: { p: LookPlace; className: string; grow?: number }) {
  return <circle className={className} cx={px(p.head.x)} cy={px(p.head.y)} r={px(p.hr + grow)} data-look-part="head" />;
}

/** From Beyond's head: black, a window onto the same still sky as its ground. */
export function VoidHead({ p, seed, id }: { p: LookPlace; seed: string; id: string }) {
  return (
    <g transform={`translate(${px(p.head.x)} ${px(p.head.y)})`} data-look-part="beyond-head">
      <VoidWindow r={p.hr} seed={seed} clip={`look-void-head-${id}`} star={0.6} />
    </g>
  );
}
